/**
 * Операции банковского счёта (карточка BL-2026-08-15-2024, §7.1 п. 4).
 *
 * Flow: счёт выбран в справочнике → GET /api/treasury/bank-transactions
 *       с отбором по счёту, виду и периоду → итоги по валютам «нашим
 *       взглядом» и страница операций.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadBankTransactions;
 *   - ./BankStatementCommon: бейджи и форматтеры.
 *
 * Связанные файлы:
 *   - src/BankAccountsPanel.jsx — откуда выбирается счёт;
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md — §6.6.
 *
 * Здесь — только ПРИНЯТЫЕ операции (каноническое множество после дедупа):
 * то, что разобрано из файла с несошедшимся инвариантом, в учёт не попало и
 * видно лишь в карточке своей загрузки.
 *
 * Ответ применяется, только если он на ПОСЛЕДНИЙ запрос (useLatestRequest):
 * при быстрой смене периода поздний ответ по старому отбору иначе встал бы
 * под новые фильтры.
 *
 * Для роли viewer сервер отдаёт реквизиты контрагента пустыми (ФИО физлиц)
 * и ставит в ответ признак `personal_data_hidden` — ячейка тогда показывает
 * «скрыто» (см. CounterpartyName).
 *
 * Счёт без `account_id` (null) операций не запрашивает: без отбора по счёту
 * сервер вернул бы операции ВСЕХ счетов под заголовком одного.
 */

import { useEffect, useState } from "react";

import { loadBankTransactions } from "./api";
import {
  CounterpartyName,
  KIND_META,
  KindBadge,
  formatAmount,
  formatDate,
  formatMoment,
  rowKey,
  useLatestRequest,
} from "./BankStatementCommon";

/** Размер страницы — умолчание сервера (§6.6). */
const PAGE = 100;

/**
 * Операции выбранного счёта.
 *
 * @param {object} account — счёт §6.7
 * @param {function(): void} onClose — закрыть панель
 */
export default function BankTransactionsPanel({ account, onClose }) {
  const [filters, setFilters] = useState({ kind: "", from: "", to: "" });
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const begin = useLatestRequest();
  // Счёт не заведён в справочник (account_id = null): отбора по нему нет,
  // а запрос без account_id вернул бы операции всех счетов.
  const noAccount = account.account_id === null || account.account_id === undefined;

  useEffect(() => {
    if (noAccount) return;
    const isCurrent = begin();
    loadBankTransactions({
      account_id: account.account_id,
      ...filters,
      limit: PAGE,
      offset,
    })
      .then((d) => {
        if (!isCurrent()) return;
        setData(d);
        setError("");
      })
      .catch((e) => {
        if (!isCurrent()) return;
        setError(e.message);
        console.error("операции банковского счёта не загрузились", account.account_id, e);
      });
  }, [begin, noAccount, account.account_id, filters, offset]);

  function setFilter(name, value) {
    setOffset(0);
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  const rows = data ? data.transactions || [] : [];
  // Признак скрытия персональных данных — на уровне ответа (§6.6).
  const hidden = Boolean(data && data.personal_data_hidden);
  const total = data ? data.total || 0 : 0;
  const shownTo = offset + rows.length;

  return (
    <section className="card">
      <h2>
        Операции счёта {account.account_number} · {account.currency}{" "}
        <button type="button" className="link bs-close" onClick={onClose}>
          закрыть
        </button>
      </h2>

      <div className="filters">
        <select value={filters.kind} onChange={(e) => setFilter("kind", e.target.value)} aria-label="вид операции">
          <option value="">все виды</option>
          {Object.entries(KIND_META).map(([code, meta]) => (
            <option key={code} value={code}>
              {meta.title}
            </option>
          ))}
        </select>
        <label className="bs-date">
          с{" "}
          <input type="date" value={filters.from} onChange={(e) => setFilter("from", e.target.value)} aria-label="дата с" />
        </label>
        <label className="bs-date">
          по{" "}
          <input type="date" value={filters.to} onChange={(e) => setFilter("to", e.target.value)} aria-label="дата по" />
        </label>
      </div>

      {noAccount && (
        <p className="muted">
          Счёт ещё не заведён в справочник — операций по нему в учёте нет.
        </p>
      )}
      {error && <p className="error">{error}</p>}
      {!data && !error && !noAccount && <p className="muted">Загрузка…</p>}

      {data && (data.totals || []).length > 0 && (
        <table className="bs-table">
          <thead>
            <tr>
              <th>Валюта</th>
              <th className="num">Поступления</th>
              <th className="num">Списания</th>
              <th className="num">Комиссии</th>
              <th className="num">Внутренние (+/−)</th>
              <th className="num">Обмены (+/−)</th>
            </tr>
          </thead>
          <tbody>
            {data.totals.map((t) => (
              <tr key={t.currency}>
                <td>{t.currency}</td>
                <td className="num">{formatAmount(t.incoming)}</td>
                <td className="num">{formatAmount(t.outgoing)}</td>
                <td className="num">{formatAmount(t.fees)}</td>
                <td className="num">
                  {formatAmount(t.internal_in)} / {formatAmount(t.internal_out)}
                </td>
                <td className="num">
                  {formatAmount(t.fx_in)} / {formatAmount(t.fx_out)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data && rows.length === 0 && <p className="muted">Операций по этому отбору нет.</p>}

      {rows.length > 0 && (
        <div className="scroll-rows">
          <table className="bs-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Контрагент</th>
                <th>Назначение</th>
                <th className="num">Сумма</th>
                <th>Вид</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tx) => (
                <tr
                  key={rowKey(tx.id, "tx", tx.bank, tx.account_number, tx.currency, tx.external_key)}
                >
                  <td>
                    {formatDate(tx.booking_date)}
                    {tx.occurred_at && (
                      <div className="cell-note muted">{formatMoment(tx.occurred_at).slice(11)}</div>
                    )}
                  </td>
                  <td>
                    <CounterpartyName tx={tx} hidden={hidden} />
                    {tx.counterparty_account && (
                      <div className="cell-note muted">{tx.counterparty_account}</div>
                    )}
                  </td>
                  <td className="bs-purpose">
                    {tx.purpose}
                    {tx.parent_external_key && (
                      <div className="cell-note">к операции {tx.parent_external_key}</div>
                    )}
                    {tx.self_named_counterparty && (
                      <div className="cell-note warn">отправитель — сама компания, счёт не назван</div>
                    )}
                  </td>
                  <td className="num">{formatAmount(tx.signed_amount, tx.currency)}</td>
                  <td>
                    <KindBadge
                      kind={tx.kind}
                      title={tx.kind_title}
                      feeKindTitle={tx.kind === "fee" ? tx.fee_kind_title : ""}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <p className="muted">
          Показано {rows.length === 0 ? 0 : offset + 1}–{shownTo} из {total}.{" "}
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Назад
          </button>{" "}
          <button type="button" disabled={shownTo >= total} onClick={() => setOffset(offset + PAGE)}>
            Вперёд
          </button>
        </p>
      )}
    </section>
  );
}
